import { useCallback, useMemo, useState } from 'react';
import type { DateRange } from 'react-day-picker';
import type { Query } from '../types';

export interface FileFilters {
  dateRange?: DateRange;
  fileTypes: Query['filter_type'];
  taskTypes: Query['task_type'];
  creatorIds: string[];
  resolutions: Query['resolution'];
  ratios: Query['ratio'];
  modelVersions: Query['model_version'];
  filterMark: boolean;
  filterFolder: boolean;
}

const DEFAULT_FILTERS: FileFilters = {
  dateRange: undefined,
  fileTypes: [],
  taskTypes: [],
  creatorIds: [],
  resolutions: [],
  ratios: [],
  modelVersions: [],
  filterMark: false,
  filterFolder: false,
};

export const useFileFilters = (initialFilters?: Partial<FileFilters>) => {
  const [filters, setFilters] = useState<FileFilters>(() => ({
    ...DEFAULT_FILTERS,
    ...initialFilters,
  }));

  /**
   * 更新筛选器的部分字段
   */
  const updateFilters = useCallback((updates: Partial<FileFilters>) => {
    setFilters((prev) => ({ ...prev, ...updates }));
  }, []);

  /**
   * 清空所有筛选器
   */
  const clearFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
  }, []);

  /**
   * 检查是否有激活的筛选器
   */
  const hasActiveFilters = useMemo(() => {
    return Object.entries(filters).some(([key, value]) => {
      const defaultValue = DEFAULT_FILTERS[key as keyof FileFilters];
      return JSON.stringify(value) !== JSON.stringify(defaultValue);
    });
  }, [filters]);

  /**
   * 清除特定筛选器字段
   */
  const clearFilter = useCallback(
    <K extends keyof FileFilters>(key: K) => {
      updateFilters({ [key]: DEFAULT_FILTERS[key] } as Partial<FileFilters>);
    },
    [updateFilters],
  );

  return {
    filters,
    updateFilters,
    clearFilters,
    clearFilter,
    hasActiveFilters,
  };
};
